<template>
    <div class="navigation-badge"
         :class="[badge.class, {'navigation-badge--large': large}]">
        {{ badge.title }}
    </div>
</template>

<script setup lang="ts">
    import { MenuBadgesEnum } from '../../enums/menuEnums';
    import { getTrans } from '@/js/helpers/getTrans';

    const props = withDefaults(defineProps<{
        badgeType: MenuBadgesEnum,
        large?: boolean,
    }>(), {
        large: false,
    });

    enum BadgeTypeEnum {
        RED = 'red',
        PALE_RED = 'pale-red',
        ORANGE = 'orange',
        GREEN = 'green',
        PURPLE = 'purple',
        BLUE = 'blue',
    }

    enum BadgeTitleKeyEnum {
        REWARDS = 'rewards',
        HOT = 'hot',
        COMING_SOON = 'comingSoon',
        TRENDING = 'trending',
        FREE = 'free',
        EARN = 'earn',
        TOP = 'top',
        EXCLUSIVE = 'exclusive',
        NEW = 'new',
    }

    enum BadgeTitleFallbackEnum {
        REWARDS = 'Rewards',
        HOT = 'Hot',
        COMING_SOON = 'Coming Soon',
        TRENDING = 'Trending',
        FREE = 'Free',
        EARN = 'Earn',
        TOP = 'Top',
        EXCLUSIVE = 'Exclusive',
        NEW = 'New',
    }

    const createBadge = (type: BadgeTypeEnum, key: MenuBadgesEnum, titleKey: BadgeTitleKeyEnum, defaultTitle: BadgeTitleFallbackEnum) => ({
        [key]: {
            title: getTrans(`header.${titleKey}`, defaultTitle),
            class: `navigation-badge--${type}`,
        },
    });

    const rewardBadge = createBadge(BadgeTypeEnum.RED, MenuBadgesEnum.REWARDS, BadgeTitleKeyEnum.REWARDS, BadgeTitleFallbackEnum.REWARDS);
    const hotBadge = createBadge(BadgeTypeEnum.RED, MenuBadgesEnum.HOT, BadgeTitleKeyEnum.HOT, BadgeTitleFallbackEnum.HOT);
    const comingSoonBadge = createBadge(BadgeTypeEnum.PALE_RED, MenuBadgesEnum.COMING_SOON, BadgeTitleKeyEnum.COMING_SOON, BadgeTitleFallbackEnum.COMING_SOON);
    const trendingBadge = createBadge(BadgeTypeEnum.ORANGE, MenuBadgesEnum.TRENDING, BadgeTitleKeyEnum.TRENDING, BadgeTitleFallbackEnum.TRENDING);
    const freeBadge = createBadge(BadgeTypeEnum.GREEN, MenuBadgesEnum.FREE, BadgeTitleKeyEnum.FREE, BadgeTitleFallbackEnum.FREE);
    const earnBadge = createBadge(BadgeTypeEnum.GREEN, MenuBadgesEnum.EARN, BadgeTitleKeyEnum.EARN, BadgeTitleFallbackEnum.EARN);
    const topBadge = createBadge(BadgeTypeEnum.PURPLE, MenuBadgesEnum.TOP, BadgeTitleKeyEnum.TOP, BadgeTitleFallbackEnum.TOP);
    const exclusiveBadge = createBadge(BadgeTypeEnum.BLUE, MenuBadgesEnum.EXCLUSIVE, BadgeTitleKeyEnum.EXCLUSIVE, BadgeTitleFallbackEnum.EXCLUSIVE);
    const newBadge = createBadge(BadgeTypeEnum.PURPLE, MenuBadgesEnum.NEW, BadgeTitleKeyEnum.NEW, BadgeTitleFallbackEnum.NEW);

    const badges = {
        ...rewardBadge,
        ...hotBadge,
        ...comingSoonBadge,
        ...trendingBadge,
        ...freeBadge,
        ...earnBadge,
        ...topBadge,
        ...exclusiveBadge,
        ...newBadge,
    };

    const badge = badges[props.badgeType];
</script>

<style scoped lang="scss">
@use "@/sass/design-system/variables" as variables;
@use "@/sass/partials/variables" as legacyVariables;

.navigation-badge {
    display: flex;
    height: min-content;
    align-items: center;
    justify-content: center;
    padding: 0 6px;
    border-radius: 4px;
    color: legacyVariables.$white;
    font-size: 8px;
    font-weight: 700;
    text-align: center;
    text-transform: capitalize;

    &--red {
        background-color: legacyVariables.$red;
    }

    &--pale-red {
        background-color: variables.$error500;
    }

    &--green {
        background-color: variables.$primary500;
    }

    &--orange {
        background-color: legacyVariables.$orange-red;
    }

    &--blue {
        background-color: legacyVariables.$dodger-blue;
    }

    &--purple {
        background-color: legacyVariables.$royal-purple;
    }

    &--large {
        font-size: 14px;
    }
}

.mobile-nav {
    .navigation-badge {
        font-size: 12px;
    }
}
</style>
